import { useEffect, useState } from 'react';

export interface UserLocation {
    latitude: number;
    longitude: number;
    accuracy?: number;
    timestamp: number;
    isMock: boolean;
}

const MOCK_START: UserLocation = {
    latitude: 37.3349, // Apple Park area as arbitrary mock
    longitude: -122.0090,
    accuracy: 15,
    timestamp: Date.now(),
    isMock: true,
};

export function useUserLocation(options?: { enableHighAccuracy?: boolean; mock?: boolean }) {
    const { enableHighAccuracy = true, mock = false } = options || {};
    const [location, setLocation] = useState<UserLocation | null>(mock ? MOCK_START : null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (mock) {
            // simple mock drift every 5s
            const interval = setInterval(() => {
                setLocation(prev => {
                    const base = prev || MOCK_START;
                    return {
                        ...base,
                        latitude: base.latitude + (Math.random() - 0.5) * 0.0005,
                        longitude: base.longitude + (Math.random() - 0.5) * 0.0005,
                        timestamp: Date.now(),
                        isMock: true,
                    };
                });
            }, 5000);
            return () => clearInterval(interval);
        }
        if (!('geolocation' in navigator)) {
            setError('Geolocation unsupported');
            return;
        }

        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                setLocation({
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    accuracy: pos.coords.accuracy,
                    timestamp: pos.timestamp,
                    isMock: false,
                });
            },
            (err) => {
                setError(err.message || 'Geolocation error');
            },
            { enableHighAccuracy, maximumAge: 5000, timeout: 10000 }
        );
        return () => navigator.geolocation.clearWatch(watchId);
    }, [enableHighAccuracy, mock]);

    return { location, error };
}
